import { TitleCasePipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import {
    TelXLDatepickerComponent,
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLMultiselectDropdownComponent,
} from '@dxp/elements';
import {
    IActivityHistoryFilterStateTypeId,
    IAgentRelations,
    IDateRangeSelection,
    IDropdown,
    IHistoricActivities,
} from '@dxp/core/models';
import {
    addMonths,
    endOfDay,
    endOfToday,
    startOfDay,
    startOfToday,
} from 'date-fns';
import { fromEvent } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { SettingsService } from '@dxp/core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChannelType, Direction } from '@dxp/core/types';
import { TelXLActivityHistoryItemComponent } from '../item/activity-history-item.component';
import { ActivityHistoryService } from '../services/activity-history.service';

const LIMIT = 20;

@Component({
    selector: 'telxl-shared-agent-activity-history-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TelXLLoadingComponent,
        TelXLActivityHistoryItemComponent,
        TelXLDropdownComponent,
        TelXLMultiselectDropdownComponent,
        TelXLDatepickerComponent,
    ],
    providers: [TitleCasePipe],
    templateUrl: './agent-activity-history-panel.component.html',
    styleUrl: './agent-activity-history-panel.component.scss',
})
export class TelXLAgentActivityHistoryPanelComponent
    implements OnInit, AfterViewInit
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #settingsService = inject(SettingsService);
    readonly #activityHistoryService = inject(ActivityHistoryService);
    readonly #titleCasePipe = inject(TitleCasePipe);

    readonly #activityHistoryState =
        this.#settingsService.agentActivityHistoryState;

    agent = input.required<string>();
    height = input.required<string>();

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    agentActivity = signal<IHistoricActivities>({
        count: 0,
        historicActivity: [],
    });

    relations = signal<IAgentRelations>({
        queues: [],
        channels: [],
        completionCodes: [],
    });
    fromDate = computed<Date>(() => this.#activityHistoryState().dateFrom);

    toDate = computed<Date | null>(() => this.#activityHistoryState().dateTo);
    today = signal<Date>(new Date());

    sort = computed<'oldest' | 'newest'>(
        () => this.#activityHistoryState().sortSelection,
    );
    queues = computed<string[]>(
        () => this.#activityHistoryState().queueOrAgentSelection,
    );
    channels = computed<number[]>(
        () => this.#activityHistoryState().channelSelection,
    );
    channelTypes = computed<ChannelType[]>(
        () => this.#activityHistoryState().channelTypesSelection,
    );
    direction = computed<Direction>(
        () => this.#activityHistoryState().directionSelection,
    );
    completionCodes = computed<string[]>(
        () => this.#activityHistoryState().completionCodeSelection,
    );

    isLoading = signal<boolean>(false);

    queueMenuItems = computed<IDropdown<string>[]>(() =>
        this.relations().queues.map(queue => ({
            label: queue.name,
            value: queue.id,
            disabled: false,
        })),
    );

    channelMenuItems = computed<IDropdown<number>[]>(() =>
        this.relations().channels.map(channel => ({
            label: channel.name,
            value: parseInt(channel.id),
            disabled: false,
        })),
    );

    mediaMenuItems = computed<IDropdown<ChannelType>[]>(() => {
        const titleCase = this.#titleCasePipe.transform.bind(
            this.#titleCasePipe,
        );
        const uniqueChannels = [
            'email',
            'facebook',
            'instagram',
            'voice',
            'webchat',
            'whatsapp',
        ] as ChannelType[];

        return uniqueChannels.map(channel => ({
            label: titleCase(channel),
            value: channel,
            disabled: false,
        }));
    });

    directionMenuItems = computed<IDropdown<Direction>[]>(() => [
        {
            label: 'Both',
            value: 'All',
            disabled: false,
        },
        {
            label: 'Inbound',
            value: 'InBound',
            disabled: false,
        },
        {
            label: 'Outbound',
            value: 'OutBound',
            disabled: false,
        },
    ]);

    sortMenuItems = computed<IDropdown<'oldest' | 'newest'>[]>(() => [
        {
            label: 'Ascending',
            value: 'oldest',
            disabled: false,
        },
        {
            label: 'Descending',
            value: 'newest',
            disabled: false,
        },
    ]);

    completionCodeMenuItems = computed<IDropdown<string>[]>(() =>
        this.relations()
            .completionCodes.filter(
                completionCode => completionCode.name.length > 0,
            )
            .map(completionCode => ({
                label: completionCode.name,
                value: completionCode.id,
                disabled: false,
            })),
    );

    ngOnInit() {
        if (this.#activityHistoryState().subjectId !== this.agent()) {
            this.clearAllFilters();
        } else {
            this.#reload();
        }
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadActivity(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }
        this.#reload();
    }

    onSortChanged(sort: 'oldest' | 'newest') {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            sortSelection: sort,
        });
        this.#reload();
    }

    onQueuesChanged(queues: string[]) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            queueOrAgentSelection: queues,
        });
        this.#reload();
    }

    onChannelsChanged(channels: number[]) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            channelSelection: channels,
        });
        this.#reload();
    }

    onChannelTypesChanged(channelTypes: ChannelType[]) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            channelTypesSelection: channelTypes,
        });
        this.#loadRelations();
        this.#reload();
    }

    onDirectionChanged(direction: Direction) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            directionSelection: direction,
        });
        this.#reload();
    }

    onCompletionCodesChanged(completionCodes: string[]) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            completionCodeSelection: completionCodes,
        });
        this.#reload();
    }

    onDateRangeSelected(range: IDateRangeSelection) {
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            dateFrom: startOfDay(range.from ?? addMonths(startOfToday(), -1)),
        });
        this.#activityHistoryState.set({
            ...this.#activityHistoryState(),
            dateTo: range.to ? endOfDay(range.to) : null,
        });

        if (this.fromDate() && this.toDate()) {
            this.#loadRelations();
            this.#reload();
        }
    }

    clearAllFilters() {
        this.#activityHistoryState.set({
            queueOrAgentSelection: [],
            channelSelection: [],
            channelTypesSelection: [],
            completionCodeSelection: [],
            directionSelection: 'All',
            sortSelection: 'newest',
            typeId: IActivityHistoryFilterStateTypeId.MY_ACTIVITY_HISTORY,
            subjectId: this.agent(),
            dateFrom: addMonths(startOfToday(), -1),
            dateTo: endOfToday(),
        });
        this.#reload();
    }

    #hasMoreData(): boolean {
        return (
            this.agentActivity().historicActivity.length <
            this.agentActivity().count
        );
    }

    #loadActivity(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        if (!this.toDate())
            this.#activityHistoryState.set({
                ...this.#activityHistoryState(),
                dateTo: endOfToday(),
            });

        this.#activityHistoryService
            .fetchAgentActivity(
                this.agent(),
                LIMIT,
                this.agentActivity().historicActivity.length,
                {
                    queues: this.queues(),
                    channels: this.channels(),
                    channelTypes: this.channelTypes(),
                    direction: this.direction(),
                    completionCodes: this.completionCodes(),
                    date: { from: this.fromDate(), to: this.toDate() },
                },
                this.sort(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.agentActivity.update(current => {
                    return {
                        count: data.count,
                        historicActivity: [
                            ...current.historicActivity,
                            ...data.historicActivity,
                        ],
                    };
                });

                this.isLoading.set(false);
            });
    }

    #reload() {
        this.agentActivity.set({
            count: 0,
            historicActivity: [],
        });

        this.#loadActivity(true);
        this.#loadRelations();
    }

    #loadRelations() {
        this.#activityHistoryService
            .fetchAgentRelations(
                this.agent(),
                this.channelTypes(),
                this.fromDate(),
                this.toDate() ?? endOfToday(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(agentRelations => {
                this.relations.set(agentRelations);
                this.#activityHistoryState.set({
                    ...this.#activityHistoryState(),
                    queueOrAgentSelection: this.queues(),
                    channelSelection: this.channels(),
                    completionCodeSelection: this.completionCodes(),
                });
            });
    }
}
